import { useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";

const LangMenu = ({ mobile = false }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === lang);
  return (
    <div className="relative">
      <button data-testid={mobile ? "language-toggle-mobile" : "language-toggle"} onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#1E352A]/20 px-3.5 py-1.5 text-[11px] tracking-wide text-[#1E352A] transition-colors duration-300 hover:border-[#F4A261] hover:text-[#F4A261]">
        <Globe size={13} /> {current?.label}
      </button>
      {open && (
        <div data-testid={mobile ? "language-menu-mobile" : "language-menu"}
          className="absolute right-0 mt-2 w-44 max-h-72 overflow-auto rounded-xl border border-[#E2E8E4] bg-[#FAF9F6] shadow-xl py-2 z-[60]">
          {LANGUAGES.map((l) => (
            <button key={l.code} data-testid={`language-option-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${l.code === lang ? "text-[#F4A261] font-semibold" : "text-[#1A2F24] hover:bg-[#C4D7D1]/40"}`}>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const links = [
    { label: t("nav_mission"), href: "#mission", key: "mission" },
    { label: t("nav_programs"), href: "#programs", key: "programs" },
    { label: t("nav_events"), href: "#events", key: "events" },
    { label: t("nav_pantry"), href: "#pantry", key: "pantry" },
    { label: t("nav_volunteer"), href: "#volunteer", key: "volunteer" },
    { label: t("nav_contact"), href: "#contact", key: "contact" },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/70 border-b border-[#E2E8E4]/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="Living Fruit Outreach logo" className="h-10 w-auto" />
          <span className="font-display text-xl leading-none">Living Fruit <em className="not-italic text-[#5C7368] text-sm font-body tracking-[0.2em] uppercase">Outreach</em></span>
        </a>
        <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.key}`}
              className="text-xs tracking-[0.2em] uppercase text-[#5C7368] hover:text-[#1A2F24] transition-colors duration-300">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangMenu />
          <a href="#donate" data-testid="nav-donate-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-[#1E352A] text-[#FAF9F6] px-5 py-2 text-xs tracking-[0.15em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white">
            {t("nav_donate")}
          </a>
          <button data-testid="nav-menu-toggle" className="md:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#E2E8E4] bg-[#FAF9F6] px-6 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.key}`}
              className="text-sm tracking-[0.2em] uppercase text-[#5C7368]">
              {l.label}
            </a>
          ))}
          <a href="https://donate.stripe.com/fZu6oH4pWaH35D9dxJ48000" target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-donate" className="text-sm tracking-[0.2em] uppercase text-[#F4A261] font-semibold">{t("nav_donate")}</a>
          <LangMenu mobile />
        </div>
      )}
    </header>
  );
};

export default Header;
